/* Night Glow: small, dependency-free motion for the ng-* sections.
   - [data-ng-reveal]   fades and lifts in when it scrolls into view (children can set --i for a stagger)
   - [data-ng-scroller] a swipeable row with arrows and a progress bar
   - [data-ng-ba]       a before/after slider you can drag
   - [data-ng-timeline] a line that fills as you scroll, steps light up
   - [data-ng-count]    a number that counts up once
   Without JS, or with reduced motion, everything simply shows in its final state. */
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;
  if (!reduce && 'IntersectionObserver' in window) root.classList.add('ng-js');

  function reveal(scope) {
    var els = (scope || document).querySelectorAll('[data-ng-reveal]:not(.is-in)');
    if (!root.classList.contains('ng-js')) { els.forEach(function (el) { el.classList.add('is-in'); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach(function (el) { io.observe(el); });
  }

  function scroller(el) {
    if (el.dataset.ngReady) return; el.dataset.ngReady = '1';
    var track = el.querySelector('[data-ng-track]');
    var prev = el.querySelector('[data-ng-prev]');
    var next = el.querySelector('[data-ng-next]');
    var bar = el.querySelector('[data-ng-bar]');
    if (!track) return;
    function step() {
      var card = track.firstElementChild;
      return card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 16) : track.clientWidth * 0.8;
    }
    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var p = max > 0 ? track.scrollLeft / max : 0;
      if (bar) bar.style.setProperty('--p', Math.max(0.08, Math.min(1, (track.clientWidth / track.scrollWidth) + p * (1 - track.clientWidth / track.scrollWidth))));
      if (prev) prev.disabled = track.scrollLeft < 4;
      if (next) next.disabled = track.scrollLeft > max - 4;
      el.classList.toggle('is-static', max <= 4);
    }
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' }); });
    track.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
    window.addEventListener('resize', update);
    // drag with a mouse on desktop (touch already scrolls natively)
    var down = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      down = true; moved = false; startX = e.clientX; startLeft = track.scrollLeft; track.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX; if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', function () { if (down) { down = false; track.classList.remove('is-dragging'); } });
    track.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    update();
  }

  function beforeAfter(el) {
    if (el.dataset.ngReady) return; el.dataset.ngReady = '1';
    var range = el.querySelector('input[type="range"]');
    function set(v) { el.style.setProperty('--pos', v + '%'); if (range) range.value = v; }
    if (range) range.addEventListener('input', function () { set(range.value); el.classList.add('is-touched'); });
    var dragging = false;
    function fromEvent(e) {
      var r = el.getBoundingClientRect();
      var x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
      set(Math.max(0, Math.min(100, (x / r.width) * 100)).toFixed(1));
    }
    el.addEventListener('pointerdown', function (e) { dragging = true; el.classList.add('is-touched'); fromEvent(e); });
    window.addEventListener('pointermove', function (e) { if (dragging) fromEvent(e); });
    window.addEventListener('pointerup', function () { dragging = false; });
    // a small hint: the handle glides once when the slider first comes into view
    if (!reduce && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return; io.disconnect();
          if (el.classList.contains('is-touched')) return;
          var t0 = null, from = 50;
          function frame(t) {
            if (el.classList.contains('is-touched')) return;
            if (!t0) t0 = t; var k = Math.min(1, (t - t0) / 1600);
            var v = from + Math.sin(k * Math.PI * 2) * 14 * (1 - k);
            set(v.toFixed(1)); if (k < 1) window.requestAnimationFrame(frame);
          }
          setTimeout(function () { window.requestAnimationFrame(frame); }, 350);
        });
      }, { threshold: 0.5 });
      io.observe(el);
    }
  }

  function timeline(list) {
    if (list.dataset.ngReady) return; list.dataset.ngReady = '1';
    var items = Array.prototype.slice.call(list.querySelectorAll('[data-ng-step]'));
    if (!items.length) return;
    if (reduce || !window.requestAnimationFrame) { items.forEach(function (i) { i.classList.add('is-active'); }); list.style.setProperty('--fill', '100%'); return; }
    list.classList.add('is-animated');
    var ticking = false;
    function update() {
      ticking = false;
      var vh = window.innerHeight || root.clientHeight;
      var anchor = vh * 0.62;
      var r = list.getBoundingClientRect();
      var atEnd = window.innerHeight + window.pageYOffset >= root.scrollHeight - 2;
      var fill = atEnd ? r.height : Math.max(0, Math.min(r.height, anchor - r.top));
      list.style.setProperty('--fill', fill + 'px');
      items.forEach(function (it) {
        var d = it.getBoundingClientRect();
        it.classList.toggle('is-active', atEnd || d.top + 18 <= anchor);
      });
    }
    function req() { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }
    window.addEventListener('scroll', req, { passive: true });
    window.addEventListener('resize', req);
    update();
  }

  function counter(el) {
    if (el.dataset.ngReady) return; el.dataset.ngReady = '1';
    var end = parseInt(el.getAttribute('data-ng-count'), 10) || 0;
    var suffix = el.getAttribute('data-ng-suffix') || '';
    if (reduce || !('IntersectionObserver' in window)) { el.textContent = end + suffix; return; }
    el.textContent = '0' + suffix;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return; io.disconnect();
        var t0 = null;
        function frame(t) {
          if (!t0) t0 = t; var k = Math.min(1, (t - t0) / 1400); var eased = 1 - Math.pow(1 - k, 3);
          el.textContent = Math.round(end * eased) + suffix; if (k < 1) window.requestAnimationFrame(frame);
        }
        window.requestAnimationFrame(frame);
      });
    }, { threshold: 0.6 });
    io.observe(el);
  }

  function init(scope) {
    var s = scope || document;
    root.classList.add('ng-ready');
    reveal(s);
    s.querySelectorAll('[data-ng-scroller]').forEach(scroller);
    s.querySelectorAll('[data-ng-ba]').forEach(beforeAfter);
    s.querySelectorAll('[data-ng-timeline]').forEach(timeline);
    s.querySelectorAll('[data-ng-count]').forEach(counter);
  }
  // Kaching flips back to the first pack when a shopper taps the pack that is already picked
  // (so a tap on the 90-Day Kit could silently leave 1 bottle in the cart). A tap on the picked pack now does nothing.
  document.addEventListener('click', function (e) {
    var bar = e.target.closest && e.target.closest('kaching-bundle .kaching-bundles__block > .kaching-bundles__bars > .kaching-bundles__bar');
    if (!bar || !bar.classList.contains('kaching-bundles__bar--selected')) return;
    if (e.target.closest('select, a, input[type="checkbox"], .kaching-bundles__bar-variant-select')) return;
    e.preventDefault(); e.stopPropagation();
  }, true);

  // Kaching's subscribe checkbox is drawn as a two-part switch: tapping the part that's already on does nothing,
  // tapping the other part flips it (Kaching's own handler does the work).
  document.addEventListener('click', function (e) {
    var bar = e.target.closest && e.target.closest('.kaching-bundles__subscriptions--checkbox > .kaching-bundles__bar');
    if (!bar) return;
    var r = bar.getBoundingClientRect();
    var left = (e.clientX - r.left) < r.width / 2;
    var on = bar.getAttribute('aria-checked') === 'true';
    if ((left && on) || (!left && !on)) { e.preventDefault(); e.stopPropagation(); }
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
